import { overlay } from "overlay-kit";
import { type FC, useState } from "react";

import { PageCategory, SectionPageCategory, coursePageCategories } from "components/server-types";
import { t } from "i18n/i18n";
import { Dialog } from "mds/components/Dialog";
import { Listbox, type ListboxOption } from "mds/components/Listbox";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { FolderIcon, PageListIcon } from "mds/icons";
import { topicSelectors } from "store/selectors";
import { useListSelector } from "store/store-hooks";

type MoveActivityToModalProps = {
  itemKind: "activity";
  currentTopicId?: string;
  onSubmit: (destinationTopicId: string) => void;
  onClose: () => void;
};

type MovePageToModalProps = {
  itemKind: "page";
  currentPageCategory: PageCategory;
  currentTopicId?: string;
  onSubmit: (destinationCategory: PageCategory, destinationTopicId?: string) => void;
  onClose: () => void;
};

type MoveToModalProps = MoveActivityToModalProps | MovePageToModalProps;

/** Move a Page or Activity within the course.
 * - A Page can be moved to another Topic or Category.
 * - An Activity can only be moved to another Topic.
 *
 * Does not allow moving within the same Topic (use drag and drop). */
export const MoveToModal: FC<MoveToModalProps> = (props) => {
  const { itemKind, onClose, onSubmit, currentTopicId } = props;
  const [selectedCategoryOrTopicId, setSelectedCategoryOrTopicId] = useState<string>();
  const topics = useListSelector(topicSelectors.filterForCurrentSection);

  const eligibleCourseCategories: SectionPageCategory[] =
    itemKind === "page"
      ? coursePageCategories.filter((category) => category !== props.currentPageCategory)
      : [];

  const eligibleTopics = currentTopicId
    ? topics.filter((topic) => topic.id !== currentTopicId)
    : topics;

  const items: ListboxOption[] = [
    ...eligibleCourseCategories.map((category) => ({
      id: category,
      title: t(`page_variant_plural.${category}`),
      iconStart: <FolderIcon />,
    })),
    ...eligibleTopics.map((topic) => ({
      id: topic.id,
      title: topic.title,
      iconStart: <PageListIcon />,
    })),
  ];

  const isValid = selectedCategoryOrTopicId !== undefined;

  const handleSubmit = () => {
    if (!isValid) return;

    if (itemKind === "page") {
      if (coursePageCategories.includes(selectedCategoryOrTopicId as SectionPageCategory)) {
        onSubmit(selectedCategoryOrTopicId as SectionPageCategory);
      } else {
        onSubmit("activity_page", selectedCategoryOrTopicId);
      }
    } else {
      onSubmit(selectedCategoryOrTopicId);
    }
  };

  const selectLabel =
    itemKind === "activity"
      ? t("move_to_modal.select_topic")
      : t("move_to_modal.select_category_or_topic");

  return (
    <div className="mt-2">
      <label className="flex flex-col gap-1">
        <span className="sr-only">{selectLabel}</span>

        <Listbox
          buttonSize="s"
          buttonText={selectedCategoryOrTopicId ? undefined : selectLabel}
          className="w-full"
          items={items}
          selectedValue={selectedCategoryOrTopicId}
          onSelectionChange={(item) => setSelectedCategoryOrTopicId(item.id)}
        />
      </label>

      <div className="mt-8 flex w-full justify-end gap-2">
        <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </SquareButton>

        <SquareButton disabled={!isValid} kind="accent" size="m" onClick={handleSubmit}>
          {itemKind === "activity"
            ? t("move_to_modal.move_activity_button")
            : t("move_to_modal.move_page_button")}
        </SquareButton>
      </div>
    </div>
  );
};

type ShowMoveActivityModalOptions = {
  currentTopicId?: string;
  onMoveTo: (topicId: string) => void;
};

// eslint-disable-next-line react-refresh/only-export-components
export const showMoveActivityModal = ({ currentTopicId, onMoveTo }: ShowMoveActivityModalOptions) =>
  overlay.open(({ isOpen, unmount }) => (
    <Dialog
      description={t("move_to_modal.description.activity")}
      open={isOpen}
      title={t("move_to_modal.title.activity")}
      onClose={unmount}
    >
      <MoveToModal
        currentTopicId={currentTopicId}
        itemKind="activity"
        onClose={unmount}
        onSubmit={(topicId) => {
          onMoveTo(topicId);
          unmount();
        }}
      />
    </Dialog>
  ));

type ShowMovePageModalOptions = {
  currentPageCategory: PageCategory;
  currentTopicId?: string;
  onMoveTo: (destinationCategory: PageCategory, destinationTopicId?: string) => void;
};

// eslint-disable-next-line react-refresh/only-export-components
export const showMovePageModal = ({
  currentPageCategory,
  currentTopicId,
  onMoveTo,
}: ShowMovePageModalOptions) =>
  overlay.open(({ isOpen, unmount }) => (
    <Dialog
      description={t("move_to_modal.description.page")}
      open={isOpen}
      title={t(`move_to_modal.title.${currentPageCategory}`)}
      onClose={unmount}
    >
      <MoveToModal
        currentPageCategory={currentPageCategory}
        currentTopicId={currentTopicId}
        itemKind="page"
        onClose={unmount}
        onSubmit={(destinationCategory, destinationTopicId) => {
          onMoveTo(destinationCategory, destinationTopicId);
          unmount();
        }}
      />
    </Dialog>
  ));
